import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '@hooks/useAuth';
import { useChat } from '@hooks/useChat';
import { AuthModal } from '@components/chatbot/AuthModal';
import { ChatMessage } from '@components/chatbot/ChatMessage';
import { ChatInput } from '@components/chatbot/ChatInput';
import { ThinkingIndicator } from '@components/chatbot/ThinkingIndicator';
import { QuotaDisplay } from '@components/chatbot/QuotaDisplay';
import { Container } from '@components/common/Container';

export const ChatbotSection: React.FC = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [initialMessage, setInitialMessage] = useState('');
  const [isAtBottom, setIsAtBottom] = useState(true);
  const { isAuthenticated, questionsRemaining } = useAuth();
  const { messages, isLoading, error, sendMessage } = useChat();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  // Check if user is scrolled to bottom (within 100px threshold)
  const checkIfAtBottom = () => {
    const container = messagesContainerRef.current;
    if (!container) return true;

    const { scrollTop, scrollHeight, clientHeight } = container;
    return scrollHeight - clientHeight - scrollTop < 100;
  };

  // Handle scroll events to track user position
  const handleScroll = () => {
    setIsAtBottom(checkIfAtBottom());
  };

  // Auto-scroll to bottom when new messages arrive (only if user is at bottom)
  useEffect(() => {
    if (isExpanded && messages.length > 0 && isAtBottom) {
      const container = messagesContainerRef.current;
      if (container) {
        // Use requestAnimationFrame + longer delay for citation rendering
        requestAnimationFrame(() => {
          setTimeout(() => {
            container.scrollTop = container.scrollHeight;
          }, 150);  // Increased from 50ms to handle citations/sources
        });
      }
    }
  }, [messages, isExpanded, isAtBottom]);

  // Attach scroll event listener to track user position
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  // Check authentication on mount
  useEffect(() => {
    if (!isAuthenticated) {
      setShowAuthModal(true);
    }
  }, [isAuthenticated]);

  const handleInitialInputClick = () => {
    if (!isAuthenticated) {
      setShowAuthModal(true);
    }
  };

  const handleInitialSend = () => {
    if (initialMessage.trim() && isAuthenticated) {
      setIsExpanded(true);
      sendMessage(initialMessage);
      setInitialMessage('');
    } else if (!isAuthenticated) {
      setShowAuthModal(true);
    }
  };

  const handleAuthModalClose = () => {
    setShowAuthModal(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleInitialSend();
    }
  };

  const scrollToBottom = () => {
    const container = messagesContainerRef.current;
    if (container) {
      container.scrollTo({
        top: container.scrollHeight,
        behavior: 'smooth'
      });
    }
  };

  // Color-coded quota display (matches QuotaDisplay component)
  const getQuotaColor = () => {
    if (questionsRemaining <= 1) return 'text-red-400';
    if (questionsRemaining <= 3) return 'text-yellow-400';
    return 'text-gray-400';
  };

  return (
    <section id="chatbot" className="relative py-8 md:py-20">
      <Container>
        {/* Auth Modal */}
        <AuthModal isOpen={showAuthModal} onClose={handleAuthModalClose} />

        {/* Collapsed State: Initial Prompt Input */}
        {!isExpanded && (
          <div className="w-full lg:w-[50vw] lg:mx-auto">
            <div className="bg-dark-lighter border border-gray-700 rounded-lg p-4 md:p-8 mb-6">
              <div className="flex items-start md:items-center gap-3 md:gap-4 mb-4 md:mb-6">
                <img
                  src="/assets/images/profile_picture.jpg"
                  alt="Kapil Gupta"
                  className="w-12 h-12 md:w-16 md:h-16 rounded-full border-2 border-primary flex-shrink-0"
                />
                <p className="text-base md:text-xl text-gray-300">
                  Welcome. Ask what truly matters to you.
                </p>
              </div>

              {/* Global Quota Counter */}
              <div className={`text-center text-xs md:text-sm ${getQuotaColor()} mb-4 md:mb-5`}>
                {questionsRemaining} question{questionsRemaining !== 1 ? 's' : ''} remaining today
              </div>

              <div className="flex gap-2">
                <input
                  type="text"
                  value={initialMessage}
                  onChange={(e) => setInitialMessage(e.target.value)}
                  onKeyDown={handleKeyDown}
                  onClick={handleInitialInputClick}
                  placeholder="Type your question..."
                  disabled={!isAuthenticated}
                  className="flex-1 bg-dark border border-gray-700 text-white rounded-lg px-3 md:px-4 py-2.5 md:py-3 focus:outline-none focus:border-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ fontSize: '16px' }}
                />
                <button
                  onClick={handleInitialSend}
                  disabled={!isAuthenticated || !initialMessage.trim()}
                  className="bg-primary hover:bg-primary-dark text-dark px-4 md:px-6 py-2.5 md:py-3 rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-w-[44px]"
                >
                  →
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Expanded State: Full Chat Window */}
        {isExpanded && (
          <div className="w-full lg:w-[50vw] lg:mx-auto max-w-full overflow-x-hidden">
            <div className="bg-dark-lighter border border-gray-700 rounded-lg overflow-hidden">
              {/* Header */}
              <div className="bg-primary text-dark p-3 md:p-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <img
                    src="/assets/images/profile_picture.jpg"
                    alt="Kapil Gupta"
                    className="w-7 h-7 md:w-8 md:h-8 rounded-full border-2 border-dark"
                  />
                  <h3 className="font-bold text-base md:text-lg">Truthspeaker</h3>
                </div>
                <button
                  onClick={() => setIsExpanded(false)}
                  className="hover:opacity-80 transition-opacity p-2 min-w-[44px] min-h-[44px] flex items-center justify-center"
                  aria-label="Minimize chatbot"
                >
                  <svg className="w-5 h-5 md:w-6 md:h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              </div>

              {/* Quota Display */}
              <QuotaDisplay />

              {/* Messages Container */}
              <div
                ref={messagesContainerRef}
                className="h-[60vh] md:h-[500px] overflow-y-auto overflow-x-hidden p-3 md:p-6 scrollbar-thin bg-dark max-w-full"
              >
                {messages.length === 0 && (
                  <div className="flex items-start gap-2 mb-4">
                    <img
                      src="/assets/images/profile_picture.jpg"
                      alt="Kapil Gupta"
                      className="w-8 h-8 md:w-10 md:h-10 rounded-full border-2 border-primary flex-shrink-0"
                    />
                    <div className="bg-dark-lighter border border-gray-700 text-gray-300 p-3 md:p-4 rounded-lg max-w-[85%] md:max-w-[80%] text-sm md:text-base">
                      Welcome. Ask what truly matters to you.
                    </div>
                  </div>
                )}

                {messages.map((message) => (
                  <ChatMessage key={message.id} message={message} />
                ))}

                {isLoading && <ThinkingIndicator />}

                {error && (
                  <div className="bg-red-500/20 border border-red-500 text-red-400 p-2.5 md:p-3 rounded-lg mb-4 text-sm md:text-base">
                    {error}
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>

              {/* Scroll to Bottom Button */}
              {!isAtBottom && messages.length > 0 && (
                <button
                  onClick={scrollToBottom}
                  className="absolute bottom-20 right-6 bg-primary hover:bg-primary-dark text-dark p-3 rounded-full shadow-lg transition-all duration-200 hover:scale-110 z-10"
                  aria-label="Scroll to bottom"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                  </svg>
                </button>
              )}

              {/* Input */}
              <div className="border-t border-gray-700">
                <ChatInput
                  onSend={sendMessage}
                  disabled={!isAuthenticated}
                  isLoading={isLoading}
                />
              </div>
            </div>
          </div>
        )}
      </Container>
    </section>
  );
};
